import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  MicrophoneIcon,
  StopIcon,
  PauseIcon,
  PlayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline'
import { aiInterviewsApi } from '../../services/api'
import useMediaRecorder, { formatRecordingTime } from '../../hooks/useMediaRecorder'
import InterviewScreenShareModal from '../../components/interview/InterviewScreenShareModal'
import { isInterviewScreenCaptureError, type InterviewScreenShareIssue } from '../../utils/interviewScreenShare'
import type { AIInterview, AIInterviewQuestion } from '../../types/interview'

export default function InterviewRecordingPage() {
  const { interviewId } = useParams<{ interviewId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const origin = searchParams.get('origin') || 'resume' // Default to resume for backwards compatibility

  // Interview state
  const [interview, setInterview] = useState<AIInterview | null>(null)
  const [questions, setQuestions] = useState<AIInterviewQuestion[]>([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [shareGateOpen, setShareGateOpen] = useState(false)
  const [shareGateVariant, setShareGateVariant] = useState<'required' | 'wrong_choice'>('required')
  const [shareIssue, setShareIssue] = useState<InterviewScreenShareIssue | null>(null)
  const [pickerCancelled, setPickerCancelled] = useState(false)
  // True while attemptCaptureAndStart() is in flight (mic permission, the
  // native share picker, and our audio-detection wait) — the button click
  // otherwise gives no feedback for a few seconds and feels unresponsive.
  const [isRequestingShare, setIsRequestingShare] = useState(false)
  // True while the native browser share-picker is actually on screen — our
  // own modal hides during this window so the recruiter never sees two
  // overlapping dialogs (our card behind the browser's own picker) at once.
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  // Recording state
  const [hasStarted, setHasStarted] = useState(false)
  const [showEndConfirmation, setShowEndConfirmation] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  // Ref to prevent multiple upload attempts
  const uploadAttemptedRef = useRef(false)

  // Called when recording auto-starts in the background after a
  // 'silent_system_audio' wait resolves (the candidate started talking) —
  // dismiss the "waiting for candidate audio" modal and mark the interview
  // as started, same as the normal immediate-success path does.
  const handleAudioRecovered = useCallback(() => {
    setShareGateOpen(false)
    setPickerCancelled(false)
    setShareIssue(null)
    setHasStarted(true)
    toast.success("Candidate audio detected — recording started.")
  }, [])

  const handlePickerOpenChange = useCallback((isOpen: boolean) => {
    setIsPickerOpen(isOpen)
  }, [])

  // Media recorder hook
  const {
    isRecording,
    isPaused,
    recordingTime,
    micAudioLevel,
    systemAudioLevel,
    recordedBlob,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    error: recorderError,
    isSupported,
  } = useMediaRecorder({ onRecovered: handleAudioRecovered, onPickerOpenChange: handlePickerOpenChange })

  // Fetch interview details
  useEffect(() => {
    const fetchInterview = async () => {
      if (!interviewId) return

      try {
        setIsLoading(true)
        const response = await aiInterviewsApi.getDetails(interviewId)
        const data = response.data as AIInterview
        setInterview(data)
        setQuestions(data.generated_questions || [])
      } catch (err: any) {
        const message = err?.response?.data?.detail || 'Failed to load interview'
        setError(message)
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchInterview()
  }, [interviewId])

  const openScreenShareGate = useCallback(() => {
    setShareIssue(null)
    setPickerCancelled(false)
    setShareGateVariant('required')
    setShareGateOpen(true)
  }, [])

  const closeScreenShareGate = useCallback(() => {
    setShareGateOpen(false)
    setPickerCancelled(false)
    setShareIssue(null)
  }, [])

  const attemptCaptureAndStart = useCallback(async () => {
    setIsRequestingShare(true)
    try {
      // Move the interview to in_progress when (re)starting a recording. Besides
      // the normal pending/questions_generated start, this also reclaims an
      // interview that was marked "failed" because the page was reloaded or
      // closed mid-recording — otherwise the upload endpoint rejects a "failed"
      // interview and the retake would stay stuck on "failed". Reclaiming lets
      // the retake follow the normal upload -> processing -> completed lifecycle.
      if (interview && ['questions_generated', 'pending', 'failed'].includes(interview.status)) {
        await aiInterviewsApi.updateStatus(interviewId!, 'in_progress')
      }
      await startRecording()
      setShareGateOpen(false)
      setPickerCancelled(false)
      setHasStarted(true)
    } catch (err) {
      if (isInterviewScreenCaptureError(err)) {
        if (err.reason === 'generic') {
          toast.error(err.message)
          setShareGateOpen(false)
          return
        }
        if (err.reason === 'cancel') {
          setPickerCancelled(true)
          setShareGateVariant('required')
          setShareGateOpen(true)
          return
        }
        setShareIssue(err.reason as InterviewScreenShareIssue)
        setShareGateVariant('wrong_choice')
        setShareGateOpen(true)
        return
      }
      toast.error(err instanceof Error ? err.message : 'Failed to start recording.')
    } finally {
      setIsRequestingShare(false)
    }
  }, [startRecording, interview, interviewId])

  // Handle end interview
  const handleEndInterview = useCallback(() => {
    setShowEndConfirmation(true)
  }, [])

  // Confirm end interview
  const confirmEndInterview = useCallback(async () => {
    stopRecording()
    setShowEndConfirmation(false)
  }, [stopRecording])

  // Cancel end confirmation
  const cancelEndConfirmation = useCallback(() => {
    setShowEndConfirmation(false)
  }, [])

  // Intercept the browser Back button while the interview is recording so it
  // behaves exactly like the "End Interview" button: prompt for confirmation,
  // then run the same stop -> upload flow on confirm. Applies to all interview
  // types (AI / Express / Standard) since they share this recording page.
  useEffect(() => {
    if (!isRecording) return

    // Seed a history entry so the first Back press fires popstate instead of leaving the page.
    window.history.pushState(null, '', window.location.href)

    const handlePopState = () => {
      // Keep the user on the recording page and surface the same end-interview confirmation.
      window.history.pushState(null, '', window.location.href)
      setShowEndConfirmation(true)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [isRecording])

  // Warn on page reload / tab close while recording, and mark the interview as
  // failed if the user goes through with it. The page is being torn down here,
  // so a custom modal can't render — browsers only allow their native "Reload
  // site? / Leave site?" confirmation via beforeunload. Because the in-memory
  // recording is lost on unload (nothing to upload), the interview can never
  // reach "completed", so we move it off "in_progress" to "failed" rather than
  // leaving it stuck. Firing on pagehide (not beforeunload) ensures we only
  // mark it failed when the page is actually torn down — if the user cancels
  // the native prompt and stays, pagehide never fires and the status is kept.
  useEffect(() => {
    if (!isRecording || !interviewId) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Required for the native prompt to appear in some browsers.
      event.returnValue = ''
    }

    const handlePageHide = () => {
      aiInterviewsApi.abandonInterviewOnUnload(interviewId)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [isRecording, interviewId])

  // Handle upload after recording stops
  useEffect(() => {
    const uploadAudio = async () => {
      // Use ref to prevent multiple upload attempts
      if (!recordedBlob || !interviewId || uploadAttemptedRef.current) return

      // Mark upload as attempted immediately to prevent re-triggers
      uploadAttemptedRef.current = true
      setIsUploading(true)

      try {
        // Create FormData for upload
        const formData = new FormData()
        formData.append('audio_file', recordedBlob, `interview_${interviewId}.webm`)

        // Upload audio
        await aiInterviewsApi.uploadAudio(interviewId, formData, (progress) => {
          setUploadProgress(progress)
        })

        toast.success('Interview recording uploaded!')

        // Navigate back to the page where interview was initiated
        // The Celery worker will process the audio in the background
        setTimeout(() => {
          if (origin === 'job' && interview?.job_id) {
            navigate(`/jobs/${interview.job_id}?tab=interview`)
          } else if (interview?.resume_id) {
            navigate(`/resumes/${interview.resume_id}?tab=interview`)
          } else if (interview?.job_id) {
            navigate(`/jobs/${interview.job_id}?tab=interview`)
          } else {
            // Fallback to results page
            navigate(`/interviews/${interviewId}/results`)
          }
        }, 1500)
      } catch (err: any) {
        const message = err?.response?.data?.detail || 'Failed to upload recording'
        toast.error(message)
        setIsUploading(false)
        // Reset the ref so user can retry manually if needed
        uploadAttemptedRef.current = false
      }
    }

    uploadAudio()
  }, [recordedBlob, interviewId, navigate])

  // Navigate to previous question
  const goToPreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    }
  }, [currentQuestionIndex])

  // Navigate to next question
  const goToNextQuestion = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }, [currentQuestionIndex, questions.length])

  // Handle cancel/exit
  const handleCancel = useCallback(() => {
    if (isRecording) {
      setShowEndConfirmation(true)
    } else {
      navigate(-1)
    }
  }, [isRecording, navigate])

  // Current question
  const currentQuestion = questions[currentQuestionIndex]

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading interview...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !interview) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Failed to Load Interview</h2>
          <p className="text-gray-400 mb-6">{error || 'Interview not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  // Browser not supported
  if (!isSupported) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Browser Not Supported</h2>
          <p className="text-gray-400 mb-6">
            This interview requires microphone and screen capture (getDisplayMedia). Use an up-to-date Chrome, Edge, or Firefox over HTTPS or localhost.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  // Uploading state
  if (isUploading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ArrowUpTrayIcon className="h-16 w-16 text-green-500 mx-auto mb-4 animate-bounce" />
          <h2 className="text-xl font-semibold text-white mb-4">Uploading Recording...</h2>
          <div className="w-full bg-gray-700 rounded-full h-3 mb-4">
            <div
              className="bg-green-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="text-gray-400">{uploadProgress}% complete</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white">AI Interview</h1>
              <p className="text-sm text-gray-400">
                {interview.candidate_name} - {interview.job_title}
              </p>
            </div>
          </div>

          {/* Recording indicator */}
          {hasStarted && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {isRecording && !isPaused && (
                  <span className="flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                )}
                {isPaused && (
                  <span className="h-3 w-3 rounded-full bg-yellow-500"></span>
                )}
                <span className="text-white font-mono text-lg">
                  {formatRecordingTime(recordingTime)}
                </span>
              </div>

              {/* Audio level indicators — separate meters for the recruiter's mic and
                  the shared call/system audio (the candidate's side), so either one
                  dropping out mid-interview is visible live instead of only being
                  discovered afterward in playback. */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1.5">
                  <span className="text-[11px] font-medium text-gray-400">You</span>
                  <div className="flex items-center space-x-1">
                    {[...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1 rounded-full transition-all duration-75 ${
                          i < Math.floor(micAudioLevel / 10)
                            ? 'bg-green-500'
                            : 'bg-gray-600'
                        }`}
                        style={{
                          height: `${8 + i * 2}px`,
                        }}
                      ></div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-1.5">
                  <span className="text-[11px] font-medium text-gray-400">Candidate</span>
                  <div className="flex items-center space-x-1">
                    {[...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1 rounded-full transition-all duration-75 ${
                          i < Math.floor(systemAudioLevel / 10)
                            ? 'bg-green-500'
                            : 'bg-gray-600'
                        }`}
                        style={{
                          height: `${8 + i * 2}px`,
                        }}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-8">
        {!hasStarted ? (
          // Pre-start screen
          <div className="text-center max-w-2xl">
            <div className="mb-8">
              <MicrophoneIcon className="h-24 w-24 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Begin</h2>
              <p className="text-gray-400 text-lg mb-8">
                Press Start Interview to review screen-share steps, then approve Entire screen + system audio in your browser.
              </p>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-medium text-white mb-4">Interview Details</h3>
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <p className="text-sm text-gray-500">Candidate</p>
                  <p className="text-white">{interview.candidate_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Position</p>
                  <p className="text-white">{interview.job_title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Questions</p>
                  <p className="text-white">{questions.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Est. Duration</p>
                  <p className="text-white">~{questions.length * 3} minutes</p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={openScreenShareGate}
              className="px-8 py-4 bg-green-600 text-white text-lg font-semibold rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-3 mx-auto"
            >
              <MicrophoneIcon className="h-6 w-6" />
              <span>Start Interview</span>
            </button>
          </div>
        ) : (
          // Interview in progress
          <div className="w-full max-w-4xl">
            {/* Progress indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </span>
                <span className="text-sm text-gray-400">
                  {Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}% complete
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Current question */}
            {currentQuestion && (
              <div className="bg-gray-800 rounded-xl p-8 mb-8">
                <div className="flex items-center space-x-3 mb-6">
                  <span className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-green-600 rounded-full text-xl font-bold text-white">
                    {currentQuestion.question_number || currentQuestionIndex + 1}
                  </span>
                  <span className="text-gray-400 text-lg">Question</span>
                </div>
                <p className="text-2xl text-white leading-relaxed mb-6">
                  {currentQuestion.question}
                </p>
                {/* Expected key points (hidden from candidate, for interviewer reference) */}
                {currentQuestion.expected_answer?.key_points && currentQuestion.expected_answer.key_points.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <p className="text-sm font-medium text-gray-400 mb-3">Expected Key Points:</p>
                    <ul className="space-y-2">
                      {currentQuestion.expected_answer.key_points.map((point, i) => (
                        <li key={i} className="flex items-start text-gray-300">
                          <span className="text-green-500 mr-2">•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Navigation and controls */}
            <div className="flex items-center justify-between">
              {/* Previous button */}
              <button
                onClick={goToPreviousQuestion}
                disabled={currentQuestionIndex === 0}
                className="flex items-center space-x-2 px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-5 w-5" />
                <span>Previous</span>
              </button>

              {/* Recording controls */}
              <div className="flex items-center space-x-4">
                {isPaused ? (
                  <button
                    onClick={resumeRecording}
                    className="p-4 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
                    title="Resume Recording"
                  >
                    <PlayIcon className="h-8 w-8" />
                  </button>
                ) : (
                  <button
                    onClick={pauseRecording}
                    className="p-4 bg-yellow-600 text-white rounded-full hover:bg-yellow-700 transition-colors"
                    title="Pause Recording"
                  >
                    <PauseIcon className="h-8 w-8" />
                  </button>
                )}

                <button
                  onClick={handleEndInterview}
                  className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <StopIcon className="h-5 w-5" />
                  <span>End Interview</span>
                </button>
              </div>

              {/* Next button */}
              <button
                onClick={goToNextQuestion}
                disabled={currentQuestionIndex === questions.length - 1}
                className="flex items-center space-x-2 px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </main>

      {shareGateOpen && !hasStarted && !isPickerOpen && (
        <InterviewScreenShareModal
          variant={shareGateVariant}
          issue={shareIssue}
          pickerCancelled={pickerCancelled}
          isSubmitting={isRequestingShare}
          onOpenShare={() => void attemptCaptureAndStart()}
          onCancel={closeScreenShareGate}
        />
      )}

      {/* End confirmation modal */}
      {showEndConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-8 max-w-md text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">End Interview?</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to end the interview? Your recording will be uploaded for processing.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={cancelEndConfirmation}
                className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Continue Interview
              </button>
              <button
                onClick={confirmEndInterview}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                End Interview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
